/**
 * <rotating-names>: shows one name at a time and moves to the next every few seconds (data-interval, in ms), with the
 * announcement bar's transition (assets/theme.js, AnnouncementBar): the old name rises 10px and fades out in 0.25s,
 * the next comes up from 10px below in 0.4s. The element's width follows the visible name, so what comes after it
 * (the verified check, "and 23K+ others purchased") moves along smoothly instead of jumping.
 *
 * <rotating-avatars> in the same social proof turns on the same beat: every picture slides one slot to the right, the
 * right one fades out past the third slot and the next one fades in on the left, so three always show. The slots come
 * from --slot (see assets/product-social-proof.css).
 *
 * Without motion the names still take turns, just without the transition. In a background tab the timer pauses.
 */

const LEAVE = { duration: 250, easing: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)', fill: 'forwards' };
const ENTER = { duration: 400, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)' };

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class RotatingNames extends HTMLElement {
  #index = 0;
  #timer;
  #onVisibility = () => (document.hidden ? this.#stop() : this.#start());

  connectedCallback() {
    this.names = Array.from(this.querySelectorAll('[data-name]'));

    if (this.names.length < 2) return;

    this.#index = Math.max(0, this.names.findIndex((name) => !name.hidden));

    // The first width is set without the ease (and again once the web font is in, which changes it), so the line
    // does not move on load
    this.#fitWidth(this.names[this.#index], { instant: true });
    document.fonts?.ready.then(() => this.#fitWidth(this.names[this.#index], { instant: true }));
    document.addEventListener('visibilitychange', this.#onVisibility);
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #start() {
    this.#stop();
    this.#timer = setInterval(() => this.#next(), Number(this.dataset.interval) || 3000);
  }

  #stop() {
    clearInterval(this.#timer);
  }

  #fitWidth(name, { instant = false } = {}) {
    if (instant) this.dataset.settling = '';

    this.style.width = `${name.getBoundingClientRect().width}px`;

    if (instant) {
      void this.offsetWidth;
      delete this.dataset.settling;
    }
  }

  async #next() {
    this.closest('.social-proof')?.querySelector('rotating-avatars')?.step();

    const from = this.names[this.#index];
    this.#index = (this.#index + 1) % this.names.length;
    const to = this.names[this.#index];

    if (prefersReducedMotion()) {
      from.hidden = true;
      to.hidden = false;
      this.#fitWidth(to);
      return;
    }

    const leave = from.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-10px)' }], LEAVE);

    try {
      await leave.finished;
    } catch {
      return;
    }

    from.hidden = true;
    leave.cancel();
    to.hidden = false;
    this.#fitWidth(to);
    to.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }], ENTER);
  }
}

class RotatingAvatars extends HTMLElement {
  #offset = 0;

  connectedCallback() {
    this.avatars = Array.from(this.querySelectorAll('.social-proof__avatar'));
    this.avatars.forEach((avatar, index) => this.#place(avatar, index < 3 ? index : -1, { instant: true }));
  }

  #place(avatar, slot, { instant = false } = {}) {
    if (instant) avatar.dataset.instant = '';

    avatar.style.setProperty('--slot', slot);
    avatar.toggleAttribute('data-visible', slot >= 0 && slot <= 2);

    if (instant) {
      void avatar.offsetWidth;
      delete avatar.dataset.instant;
    }
  }

  step() {
    const count = this.avatars?.length ?? 0;

    if (count < 4) return;

    const at = (index) => this.avatars[(index + count) % count];
    const entering = at(this.#offset - 1);
    const leaving = at(this.#offset + 2);

    // The next picture starts one slot left of the first, out of sight, then slides in with the others
    this.#place(entering, -1, { instant: true });
    this.#place(entering, 0);
    this.#place(at(this.#offset), 1);
    this.#place(at(this.#offset + 1), 2);
    this.#place(leaving, 3);

    // Once it has faded out past the third slot it waits on the left again, unseen
    setTimeout(() => {
      if (leaving.style.getPropertyValue('--slot') === '3') this.#place(leaving, -1, { instant: true });
    }, 450);

    this.#offset = (this.#offset - 1 + count) % count;
  }
}

if (!window.customElements.get('rotating-avatars')) {
  window.customElements.define('rotating-avatars', RotatingAvatars);
}

if (!window.customElements.get('rotating-names')) {
  window.customElements.define('rotating-names', RotatingNames);
}
